import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserIdToken } from "../utils/auth";
import { exerciseCache } from "../utils/ExerciseCache";

// Base URL for the API
const API_BASE_URL = "https://app.mighty45.com/api/v1";

/**
 * Helper function to make authenticated API calls
 */
const makeApiCall = async (endpoint, options = {}) => {
  console.log("🟢 makeApiCall called for endpoint:", endpoint);

  try {
    const token = await getUserIdToken();
    console.log("🟢 Got user token, length:", token ? token.length : 0);

    const defaultOptions = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    };

    console.log("🟢 Making API call to:", `${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
    });

    console.log("🟢 API response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("🔴 API error:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    console.log("🟢 API call successful, data keys:", Object.keys(result));
    return result;
  } catch (error) {
    console.error("🔴 makeApiCall error:", error);
    throw error;
  }
};

/**
 * Helper function to make public API calls (no authentication required)
 */
const makePublicApiCall = async (endpoint, options = {}) => {
  console.log("🟡 makePublicApiCall called for endpoint:", endpoint);

  try {
    const defaultOptions = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    console.log("🟡 Making public API call to:", `${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
    });

    console.log("🟡 Public API response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("🔴 Public API error:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    console.log("🟡 Public API call successful, data keys:", Object.keys(result));
    return result;
  } catch (error) {
    console.error("🔴 makePublicApiCall error:", error);
    throw error;
  }
};

/**
 * React Query hook to fetch exercises list with BigQuery
 * @param {Object} options - Query options
 * @param {string} options.search - Search term for exercise names and descriptions
 * @param {string} options.type - Exercise type filter ('all', 'strength', 'cardio', etc.)
 * @param {number} options.limit - Number of exercises per page (default: 100)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {boolean} options.enabled - Whether the query should run (default: true)
 */
export const useExercisesBigQuery = ({
  search = "",
  type = "all",
  limit = 100,
  offset = 0,
  enabled = true,
  organization_id = null,
} = {}) => {
  return useQuery({
    queryKey: ["exercises-catalog-v2", { search, type, limit, offset, organization_id }],
    queryFn: async () => {
      console.log("🔵 BigQuery exercises hook called with params:", {
        search,
        type,
        limit,
        offset,
        organization_id,
      });
      console.log("🔵 Query key:", [
        "exercises-catalog-v2",
        { search, type, limit, offset, organization_id },
      ]);

      const params = new URLSearchParams({
        search,
        type,
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (organization_id) {
        params.set("organization_id", organization_id);
      }

      const result = await makeApiCall(`/exercises-catalog-v2?${params}`);
      console.log(
        "🔵 BigQuery exercises API response received, exercise count:",
        result?.data?.exercises?.length || 0,
      );
      
      // Populate exerciseCache for getExerciseNameByIdBigQuery
      if (result?.data?.exercises) {
        result.data.exercises.forEach((exercise) => {
          exerciseCache.set(exercise.id, exercise);
        });
      }
      
      return result;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - cache data for faster subsequent searches
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in memory longer for better UX
  });
};

/**
 * React Query hook to fetch exercises list with BigQuery (public access, no auth required)
 * @param {Object} options - Query options
 * @param {string} options.search - Search term for exercise names and descriptions
 * @param {string} options.type - Exercise type filter ('all', 'strength', 'cardio', etc.)
 * @param {number} options.limit - Number of exercises per page (default: 100)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {boolean} options.enabled - Whether the query should run (default: true)
 */
export const useExercisesBigQueryPublic = ({
  search = "",
  type = "all", 
  limit = 100,
  offset = 0,
  enabled = true,
  organization_id = null,
} = {}) => {
  // Differentiate cache times: shorter for org-specific queries (custom exercises can be added)
  // Longer for global-only queries (global exercises rarely change)
  const isOrgSpecific = !!organization_id;
  const staleTime = isOrgSpecific 
    ? 5 * 60 * 1000  // 5 minutes for org-specific (allows new custom exercises to appear)
    : 30 * 60 * 1000; // 30 minutes for global-only (global exercises rarely change)
  
  return useQuery({
    queryKey: ["exercises-catalog-v2-public", { search, type, limit, offset, organization_id }],
    queryFn: async () => {
      console.log("🟡 Public BigQuery exercises hook called with params:", {
        search,
        type,
        limit,
        offset,
        organization_id,
      });

      const params = new URLSearchParams({
        search,
        type,
        limit: limit.toString(),
        offset: offset.toString(),
      });

      // Add organization_id if provided
      if (organization_id) {
        params.set('organization_id', organization_id);
      }

      // First try the public endpoint
      try {
        const result = await makePublicApiCall(`/public/exercises-catalog-v2?${params}`);
        console.log(
          "🟡 Public BigQuery exercises API response received, exercise count:",
          result?.data?.exercises?.length || 0,
        );
        
        // Populate exerciseCache for getExerciseNameByIdBigQuery
        if (result?.data?.exercises) {
          result.data.exercises.forEach((exercise) => {
            exerciseCache.set(exercise.id, exercise);
          });
        }
        
        return result;
      } catch (error) {
        // If public endpoint fails, fallback to regular endpoint
        console.log("🟡 Public endpoint failed, trying regular endpoint:", error.message);
        const result = await makePublicApiCall(`/public/exercises-catalog-v2?${params}`);
        console.log(
          "🟡 Fallback BigQuery exercises API response received, exercise count:",
          result?.data?.exercises?.length || 0,
        );
        
        // Populate exerciseCache for getExerciseNameByIdBigQuery
        if (result?.data?.exercises) {
          result.data.exercises.forEach((exercise) => {
            exerciseCache.set(exercise.id, exercise);
          });
        }
        
        return result;
      }
    },
    enabled,
    staleTime, // Dynamic based on query type
    gcTime: 60 * 60 * 1000, // 60 minutes - keep in memory longer for better UX
  });
};

/**
 * React Query hook to fetch a single exercise by ID with BigQuery
 * @param {string} exerciseId - Exercise ID to fetch
 * @param {boolean} enabled - Whether the query should run (default: true)
 */
export const useExerciseBigQuery = (exerciseId, enabled = true) => {
  return useQuery({
    queryKey: ["exercise-catalog-v2", exerciseId],
    queryFn: async () => {
      if (!exerciseId) {
        throw new Error("Exercise ID is required");
      }
      return makeApiCall(`/exercises-catalog-v2/${exerciseId}`);
    },
    enabled: enabled && !!exerciseId,
    staleTime: 30 * 1000, // 30 seconds - shorter for immediate updates
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
  });
};

/**
 * React Query hook to create or update a custom exercise with BigQuery
 */
export const useUpdateExerciseBigQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ exerciseId, exerciseData }) => {
      if (!exerciseId) {
        throw new Error("Exercise ID is required");
      }

      return makeApiCall(`/exercises-catalog-v2/${exerciseId}`, {
        method: "PUT",
        body: JSON.stringify(exerciseData),
      });
    },
    onSuccess: (data, variables) => {
      console.log("✅ Exercise update successful, updating cache...");

      // Method 1: Optimistically update the single exercise cache
      queryClient.setQueryData(
        ["exercise-catalog-v2", variables.exerciseId],
        data,
      );

      // Method 2: Update localStorage cache with new exercise data
      if (data?.data?.exercise) {
        exerciseCache.update(variables.exerciseId, data.data.exercise);
      }

      // Method 3: Invalidate and refetch paginated exercise lists to show the updated exercise
      // Invalidate both authenticated and public caches
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2"],
        exact: false,
        refetchType: "active", // Refetch active queries to update the UI immediately
      });
      
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2-public"],
        exact: false,
        refetchType: "active", // Also invalidate public cache used by training plans
      });

      console.log(
        "🔄 Cache update completed for exercise:",
        variables.exerciseId,
      );
    },
    onError: (error) => {
      console.error("Error updating exercise:", error);
    },
  });
};

/**
 * React Query hook to create a new custom exercise with BigQuery
 */
export const useCreateCustomExerciseBigQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ exerciseId, exerciseData }) => {
      if (!exerciseId || !exerciseData) {
        throw new Error("Exercise ID and exercise data are required");
      }

      // Use PUT endpoint which handles both create and update via MERGE
      return makeApiCall(`/exercises-catalog-v2/${exerciseId}`, {
        method: "PUT",
        body: JSON.stringify(exerciseData),
      });
    },
    onSuccess: (data, variables) => {
      console.log("✅ Exercise creation successful, updating cache...");

      // Optimistically update the single exercise cache
      queryClient.setQueryData(
        ["exercise-catalog-v2", variables.exerciseId],
        data,
      );

      // Update localStorage cache with new exercise data
      if (data?.data?.exercise) {
        exerciseCache.update(variables.exerciseId, data.data.exercise);
      } else if (variables.exerciseData) {
        // Fallback: use the exerciseData we sent if response doesn't have it
        exerciseCache.update(variables.exerciseId, variables.exerciseData);
      }

      // Invalidate and refetch paginated exercise lists to show the new exercise
      // Invalidate both authenticated and public caches
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2"],
        exact: false,
        refetchType: "active",
      });
      
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2-public"],
        exact: false,
        refetchType: "active", // Also invalidate public cache used by training plans
      });

      console.log(
        "🔄 Cache update completed for new exercise:",
        variables.exerciseId,
      );
    },
    onError: (error) => {
      console.error("Error creating exercise:", error);
    },
  });
};

/**
 * React Query hook to delete a custom exercise with BigQuery
 */
export const useDeleteCustomExerciseBigQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exerciseId) => {
      if (!exerciseId) {
        throw new Error("Exercise ID is required");
      }

      return makeApiCall(`/exercises-catalog-v2/${exerciseId}`, {
        method: "DELETE",
      });
    },
    onSuccess: (data, exerciseId) => {
      console.log("✅ Exercise soft delete successful, updating cache...");

      // Remove the specific exercise from React Query cache immediately
      queryClient.removeQueries({
        queryKey: ["exercise-catalog-v2", exerciseId],
        exact: true,
      });

      // Remove from localStorage cache
      exerciseCache.remove(exerciseId);

      // Since we're using soft delete (is_deleted = TRUE), the change is immediately
      // consistent in BigQuery, so we can invalidate cache immediately
      // Invalidate both authenticated and public caches
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2"],
        exact: false,
        refetchType: "all", // Force refetch to get updated data
      });
      
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2-public"],
        exact: false,
        refetchType: "all", // Also invalidate public cache used by training plans
      });

      // Also invalidate any related queries that might depend on the exercise list
      queryClient.invalidateQueries({
        queryKey: ["exercise-count"],
        exact: false,
      });

      console.log(
        "🔄 Cache update completed for soft deleted exercise:",
        exerciseId,
      );
    },
    onError: (error) => {
      console.error("Error deleting exercise:", error);
    },
  });
};

/**
 * Hook to get exercise name by ID using BigQuery cache
 * This is a lightweight replacement for getExerciseNameById
 * @param {string} exerciseId - Exercise ID
 */
export const useExerciseNameBigQuery = (exerciseId) => {
  const { data: exerciseData } = useExerciseBigQuery(exerciseId, !!exerciseId);

  return {
    exerciseName: exerciseData?.data?.exercise?.name || "Unknown Exercise",
    isLoading: !exerciseData && !!exerciseId,
  };
};

/**
 * Hook for paginated exercises with BigQuery - replacement for usePaginatedExercises
 * @param {Object} options - Configuration options
 * @param {string} options.filter - Muscle group filter (converted to search)
 * @param {string} options.search - Search term for exercise names and descriptions
 * @param {string} options.type - Exercise type filter
 * @param {number} options.pageSize - Number of exercises per page
 * @param {number} options.currentPage - Current page (0-indexed)
 * @param {boolean} options.enabled - Whether the query should run
 */
export const usePaginatedExercisesBigQuery = ({
  filter = "all",
  search = "",
  type = "all",
  pageSize = 25,
  currentPage = 0,
  enabled = true,
} = {}) => {
  console.log("🟡 usePaginatedExercisesBigQuery called with:", {
    filter,
    search,
    type,
    pageSize,
    currentPage,
    enabled,
  });

  // Convert muscle group filter to search term if needed
  const searchTerm = filter !== "all" ? `${search} ${filter}`.trim() : search;

  const offset = currentPage * pageSize;

  const { data, isLoading, error, refetch } = useExercisesBigQuery({
    search: searchTerm,
    type,
    limit: pageSize,
    offset,
    enabled,
  });

  console.log("🟡 usePaginatedExercisesBigQuery result:", {
    exerciseCount: data?.data?.exercises?.length || 0,
    totalRows: data?.data?.pagination?.total || 0,
    isLoading,
    error: error?.message || null,
    hasData: !!data,
  });

  // Transform the response to match the old usePaginatedExercises interface
  return React.useMemo(
    () => ({
      exercises: data?.data?.exercises || [],
      loading: isLoading,
      error,
      totalRows: data?.data?.pagination?.total || 0,
      currentPage,
      hasMore: data?.data?.pagination?.hasMore || false,
      refetch,
      // Pagination functions
      nextPage: () => currentPage + 1,
      prevPage: () => Math.max(0, currentPage - 1),
      setPage: (page) => page,
    }),
    [data, isLoading, error, currentPage, refetch],
  );
};

/**
 * Prefetch exercise data for performance
 * @param {string} exerciseId - Exercise ID to prefetch
 */
export const usePrefetchExercise = () => {
  const queryClient = useQueryClient();

  return (exerciseId) => {
    queryClient.prefetchQuery({
      queryKey: ["exercise-catalog-v2", exerciseId],
      queryFn: () => makeApiCall(`/exercises-catalog-v2/${exerciseId}`),
      staleTime: 5 * 60 * 1000,
    });
  };
};

/**
 * Create a new custom exercise for the organization
 * @returns {Object} Mutation object with mutate function
 */
export const useCreateExercise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exerciseData) => {
      console.log("🟡 Creating new exercise:", exerciseData.name);
      console.log(
        "🟡 Full exercise data being sent to API:",
        JSON.stringify(exerciseData, null, 2),
      );

      return makeApiCall("/exercises-catalog-v2", {
        method: "POST",
        body: JSON.stringify(exerciseData),
      });
    },
    onSuccess: (data) => {
      console.log("✅ Exercise created successfully:", data);

      // Update localStorage cache with new exercise data
      if (data?.data?.exercise) {
        exerciseCache.update(data.data.exercise.id, data.data.exercise);
        queryClient.setQueryData(
          ["exercise-catalog-v2", data.data.exercise.id],
          data,
        );
      }

      // Invalidate ALL exercises queries to include new exercise
      // Invalidate both authenticated and public caches
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2"],
        exact: false,
      });
      
      queryClient.invalidateQueries({
        queryKey: ["exercises-catalog-v2-public"],
        exact: false, // Also invalidate public cache used by training plans
      });

      // Also force refetch any currently active queries
      queryClient.refetchQueries({
        queryKey: ["exercises-catalog-v2"],
        exact: false,
      });
      
      queryClient.refetchQueries({
        queryKey: ["exercises-catalog-v2-public"],
        exact: false, // Also refetch public queries
      });

      console.log(
        "🔄 Cache invalidation and refetch completed for new exercise",
      );
    },
    onError: (error) => {
      console.error("❌ Failed to create exercise:", error);
    },
  });
};
